import React, { useState, useEffect, useRef } from 'react'
import { BarChart3, Users, DollarSign, Clock, Eye, Search, Download, Trash2, Shield, Edit, Save, X, AlertCircle, Plus, Database, RefreshCw, Link2, UserX, UserCheck, Copy, Upload, FileText } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'

const AdminDashboard = () => {
  const { isAuthenticated, isAdmin, user, loading: authLoading } = useAuth()
  const [orders, setOrders] = useState([])
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    otherOrders: 0,
    ioTotal: 0,
    rfpCount: 0,
  })
  const [loading, setLoading] = useState(true)
  const [ordersFetchError, setOrdersFetchError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [recordKindFilter, setRecordKindFilter] = useState('all')
  const [activeTab, setActiveTab] = useState('orders')
  const [adminUsers, setAdminUsers] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviting, setInviting] = useState(false)
  // New invite fields
  const [inviteAccountType, setInviteAccountType] = useState('paying')
  const [inviteVenueQuery, setInviteVenueQuery] = useState('')
  const [inviteVenueOptions, setInviteVenueOptions] = useState([])
  const [inviteVenueBrowse, setInviteVenueBrowse] = useState([])
  const [inviteVenueUids, setInviteVenueUids] = useState([]) // Changed to array for multi-selection
  const [inviteVenueMap, setInviteVenueMap] = useState({}) // Map of UID to venue object for display
  const [inviteVenueLoading, setInviteVenueLoading] = useState(false)
  const [inviteVenuePage, setInviteVenuePage] = useState(0)
  const [inviteVenueMenuOpen, setInviteVenueMenuOpen] = useState(false)
  const [inventoryStatus, setInventoryStatus] = useState({ configured: true, connected: true, count: null })
  
  // Database fix state
  const [showDbFix, setShowDbFix] = useState(false)
  const [dbFixLoading, setDbFixLoading] = useState(false)
  const [dbFixResult, setDbFixResult] = useState(null)
  
  // Edit user modal state
  const [editUser, setEditUser] = useState(null)
  const [editAccountType, setEditAccountType] = useState('paying')
  const [editVenueQuery, setEditVenueQuery] = useState('')
  const [editVenueOptions, setEditVenueOptions] = useState([]) // unused after switch to dropdown; kept to minimize diff
  const [editVenueBrowse, setEditVenueBrowse] = useState([])
  const [editVenueLoading, setEditVenueLoading] = useState(false)
  const [editVenuePage, setEditVenuePage] = useState(0)
  const [editVenueUid, setEditVenueUid] = useState('')
  const [editVenueQueryDebounced, setEditVenueQueryDebounced] = useState('')
  // Selecting an option sets editVenueQuery to its full label, which otherwise re-triggers the
  // search effect below and flashes "No venues matched this search" for a query that was
  // never meant to be searched (the label text, not a search term). This ref lets that one
  // resulting effect run be skipped without touching the debounce timing.
  const editVenueSkipNextSearchRef = useRef(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editVenueMenuOpen, setEditVenueMenuOpen] = useState(false)
  const [editEmail, setEditEmail] = useState('')
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editExistingVenues, setEditExistingVenues] = useState([]) // All venue associations for this email
  const [editNewVenueUids, setEditNewVenueUids] = useState([]) // Venue UIDs to add
  const [editNewVenueMap, setEditNewVenueMap] = useState({}) // Map of UID to venue object for display
  const [editNewVenueMenuOpen, setEditNewVenueMenuOpen] = useState(false)
  const [editAddingVenues, setEditAddingVenues] = useState(false)
  const [editNewVenueQuery, setEditNewVenueQuery] = useState('') // Separate query for "Add Additional" section
  const [editNewVenueQueryDebounced, setEditNewVenueQueryDebounced] = useState('')
  const [editNewVenueOptions, setEditNewVenueOptions] = useState([])
  const [editNewVenueBrowse, setEditNewVenueBrowse] = useState([])
  const [editNewVenueLoading, setEditNewVenueLoading] = useState(false)
  const [editNewVenuePage, setEditNewVenuePage] = useState(0)
  const [pmpCatalog, setPmpCatalog] = useState([])
  const [invitePmpIds, setInvitePmpIds] = useState([])
  const [editPmpIds, setEditPmpIds] = useState([])

  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [bulkImportFile, setBulkImportFile] = useState(null)
  const [bulkImportLoading, setBulkImportLoading] = useState(false)
  const [bulkImportResult, setBulkImportResult] = useState(null)
  const [bulkResendLoading, setBulkResendLoading] = useState(false)

  // Helper function to format account type display
  const formatAccountType = (accountType) => {
    switch (accountType) {
      case 'admin':
        return 'Admin (staff)'
      case 'venue':
        return 'Venue'
      case 'affiliate':
        return 'Affiliate'
      case 'paying':
      default:
        return 'Brand/Agency (paying)'
    }
  }

  useEffect(() => {
    // Fetch data if user is authenticated and is admin OR venue user
    // Venue users can see orders but not manage users
    if (isAuthenticated && (isAdmin || (user && user.userType === 'venue'))) {
      fetchDashboardData()
      if (isAdmin) {
        fetchUsers()
      }
    } else if (!authLoading) {
      setLoading(false)
    }
  }, [isAuthenticated, isAdmin, authLoading, user, recordKindFilter])

  useEffect(() => {
    if (user?.userType === 'venue') {
      setRecordKindFilter('io')
    }
  }, [user?.userType])

  const fetchDashboardData = async () => {
    try {
      setOrdersFetchError(null)
      const kindQuery =
        recordKindFilter === 'all' ? '' : `?recordKind=${encodeURIComponent(recordKindFilter)}`
      // Fetch orders using the working API endpoint
      const ordersResponse = await fetch(`/api/orders${kindQuery}`, {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`,
          'Cache-Control': 'no-cache'
        }
      })
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json()
        if (Array.isArray(ordersData)) {
          setOrders(ordersData)
        } else {
          setOrders([])
          setOrdersFetchError(ordersData?.error || 'Orders API returned an unexpected response.')
        }
      } else {
        let errMsg = `Could not load orders (${ordersResponse.status}).`
        try {
          const errBody = await ordersResponse.json()
          if (errBody?.error) errMsg = errBody.error
        } catch (_) { /* ignore */ }
        setOrders([])
        setOrdersFetchError(errMsg)
      }

      // Fetch analytics using the working API endpoint
      const analyticsResponse = await fetch('/api/analytics/dashboard', {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`,
          'Cache-Control': 'no-cache'
        }
      })
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json()
        setStats({
          totalOrders: analyticsData.totalOrders,
          totalRevenue: analyticsData.totalBudget,
          pendingOrders: analyticsData.pendingOrders,
          completedOrders: analyticsData.approvedOrders,
          otherOrders: analyticsData.otherOrders ?? 0,
          ioTotal: analyticsData.ioTotal ?? analyticsData.totalOrders,
          rfpCount: analyticsData.rfpCount ?? 0,
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setOrdersFetchError(error.message || 'Failed to load dashboard data.')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const resp = await fetch('/api/admin/users')
      if (resp.ok) {
        const data = await resp.json()
        setAdminUsers(data)
      }
    } catch (e) {
      console.error('Error fetching users:', e)
    }
  }

  const fetchPmpCatalog = async () => {
    try {
      const r = await fetch('/api/admin/pmp-catalog', { credentials: 'include' })
      if (r.ok) setPmpCatalog(await r.json())
    } catch (_) {
      setPmpCatalog([])
    }
  }

  useEffect(() => {
    if (activeTab === 'users') fetchPmpCatalog()
  }, [activeTab])

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (e) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        return ok
      } catch {
        return false
      }
    }
  }

  // ------- Venue search helpers -------
  const searchInventoryVenues = async (q) => {
    if (!q || q.trim().length < 2) return []
    try {
      const resp = await fetch(`/api/inventory/venues/search?query=${encodeURIComponent(q.trim())}`)
      if (!resp.ok) return []
      const data = await resp.json()
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const listInventoryVenues = async (page = 0, pageSize = 50) => {
    try {
      const resp = await fetch(`/api/inventory/venues/list?limit=${pageSize}&offset=${page * pageSize}`)
      if (!resp.ok) return []
      const data = await resp.json()
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  }

  const fetchInventoryStatus = async () => {
    try {
      const resp = await fetch('/api/inventory/status')
      if (!resp.ok) return
      const data = await resp.json()
      setInventoryStatus(data)
    } catch {}
  }

  useEffect(() => {
    fetchInventoryStatus()
  }, [])

  useEffect(() => {
    let cancelled = false
    if (inviteAccountType === 'venue') {
      const run = async () => {
        setInviteVenueLoading(true)
        if (!inviteVenueQuery || inviteVenueQuery.length < 2) {
          // Prefetch browse list when no query
          const list = await listInventoryVenues(inviteVenuePage)
          if (!cancelled) {
            setInviteVenueBrowse(list)
            setInviteVenueOptions([])
            // Open menu if user has manually opened it (by clicking arrow or focusing)
            // but don't auto-open when account type changes
          }
        } else {
          const opts = await searchInventoryVenues(inviteVenueQuery)
          if (!cancelled) { setInviteVenueOptions(opts); setInviteVenueMenuOpen(true) }
        }
        setInviteVenueLoading(false)
      }
      run()
    } else {
      setInviteVenueOptions([])
      setInviteVenueBrowse([])
      setInviteVenueMenuOpen(false)
    }
    return () => { cancelled = true }
  }, [inviteVenueQuery, inviteAccountType, inviteVenuePage])

  // Prefetch first page when modal opens for venue accounts
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!(editUser && editAccountType === 'venue')) {
        setEditVenueBrowse([]); setEditVenueOptions([]); setEditVenuePage(0);
        return
      }
      setEditVenueLoading(true)
      try {
        const list = await listInventoryVenues(0)
        if (!cancelled) {
          setEditVenueBrowse(list)
          setEditVenueOptions([])
          setEditVenuePage(0)
          // Don't auto-open menu, let user click/focus to open
        }
      } catch {
        if (!cancelled) {
          setEditVenueBrowse([])
          setEditVenueOptions([])
        }
      } finally {
        setEditVenueLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [editUser, editAccountType])

  // Debounce edit venue search input
  useEffect(() => {
    const t = setTimeout(() => setEditVenueQueryDebounced(editVenueQuery.trim()), 250)
    return () => clearTimeout(t)
  }, [editVenueQuery])

  // Search as user types in edit modal
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!(editUser && editAccountType === 'venue')) return
      if (editVenueSkipNextSearchRef.current) {
        // This run was triggered by selecting an option, not by typing -- skip it
        // so the dropdown doesn't reopen showing a bogus "No venues matched" for
        // the label text we just set as the query.
        editVenueSkipNextSearchRef.current = false
        return
      }
      setEditVenueLoading(true)
      try {
        if (!editVenueQueryDebounced || editVenueQueryDebounced.length < 2) {
          const list = await listInventoryVenues(0)
          if (!cancelled) {
            setEditVenueBrowse(list)
            setEditVenueOptions([])
            setEditVenuePage(0)
            setEditVenueMenuOpen(true)
          }
        } else {
          const opts = await searchInventoryVenues(editVenueQueryDebounced)
          if (!cancelled) { setEditVenueOptions(opts); setEditVenueMenuOpen(true) }
        }
      } catch {}
      finally {
        if (!cancelled) setEditVenueLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [editVenueQueryDebounced, editUser, editAccountType])

  // Format label as "Publisher — Venue" and strip trailing [UID] if present
  const formatVenueLabel = (v) => {
    const pub = v.publisherName || v.publisher || v.operatorName || ''
    const venue = v.venueName || v.name || ''
    const fallback = (v.label || '').replace(/\s*\[[^\]]+\]\s*$/, '')
    if (pub && venue) return `${pub} — ${venue}`
    if (venue && !pub) return fallback || venue
    if (pub && !venue) return pub
    return fallback
  }

  /** When query has 2+ chars we show API search results only — never fall back to browse (avoids misleading unrelated venues). */
  const inviteVenueSearchActive = inviteVenueQuery.trim().length >= 2
  const inviteVenueDisplayList = inviteVenueSearchActive ? inviteVenueOptions : inviteVenueBrowse

  const editVenueSearchActive = (editVenueQueryDebounced || '').length >= 2
  const editVenueDisplayList = editVenueSearchActive ? editVenueOptions : editVenueBrowse

  const editNewVenueSearchActive = (editNewVenueQueryDebounced || '').length >= 2
  const editNewVenueDisplayList = editNewVenueSearchActive ? editNewVenueOptions : editNewVenueBrowse

  const loadMoreEditVenues = async () => {
    try {
      setEditVenueLoading(true)
      const nextPage = editVenuePage + 1
      const list = await listInventoryVenues(nextPage)
      setEditVenueBrowse(prev => [...prev, ...list])
      setEditVenuePage(nextPage)
    } catch {
      // ignore
    } finally {
      setEditVenueLoading(false)
    }
  }

  const loadMoreEditNewVenues = async () => {
    try {
      setEditNewVenueLoading(true)
      const nextPage = editNewVenuePage + 1
      const list = await listInventoryVenues(nextPage)
      setEditNewVenueBrowse(prev => [...prev, ...list])
      setEditNewVenuePage(nextPage)
    } catch {
      // ignore
    } finally {
      setEditNewVenueLoading(false)
    }
  }

  // Debounce new venue search input
  useEffect(() => {
    const t = setTimeout(() => setEditNewVenueQueryDebounced(editNewVenueQuery.trim()), 250)
    return () => clearTimeout(t)
  }, [editNewVenueQuery])

  // Search as user types in "Add Additional" section
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!editEmail) return
      setEditNewVenueLoading(true)
      try {
        if (!editNewVenueQueryDebounced || editNewVenueQueryDebounced.length < 2) {
          const list = await listInventoryVenues(0)
          if (!cancelled) {
            setEditNewVenueBrowse(list)
            setEditNewVenueOptions([])
            setEditNewVenuePage(0)
            setEditNewVenueMenuOpen(true)
          }
        } else {
          const opts = await searchInventoryVenues(editNewVenueQueryDebounced)
          if (!cancelled) { setEditNewVenueOptions(opts); setEditNewVenueMenuOpen(true) }
        }
      } catch {}
      finally {
        if (!cancelled) setEditNewVenueLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [editNewVenueQueryDebounced, editEmail])

  const reloadEditVenues = async () => {
    try {
      setEditVenueLoading(true)
      const list = await listInventoryVenues(0)
      setEditVenueBrowse(list)
      setEditVenuePage(0)
    } catch {
      setEditVenueBrowse([])
    } finally {
      setEditVenueLoading(false)
    }
  }

  // NEW: Database fix function
  const handleDatabaseFix = async () => {
    setDbFixLoading(true)
    setDbFixResult(null)
    
    try {
      const response = await fetch('/api/admin/fix-database', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        }
      })
      
      const result = await response.json()
      setDbFixResult(result)
      
      if (result.success) {
        // Refresh dashboard data after successful fix
        setTimeout(() => {
          fetchDashboardData()
        }, 1000)
      }
      
    } catch (error) {
      console.error('Error fixing database:', error)
      setDbFixResult({
        success: false,
        error: 'Failed to connect to database fix endpoint'
      })
    } finally {
      setDbFixLoading(false)
    }
  }

  const getAuthToken = async () => {
    try {
      // Use your existing auth context method
      if (typeof getAccessToken === 'function') {
        return await getAccessToken()
      }
      // Fallback for development
      return 'placeholder-token'
    } catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  }

  const trafficToControl = async (orderId) => {
    if (!window.confirm('Approve this order and queue the Broadsign Control campaign write?')) return
    try {
      const response = await fetch(`/api/io/campaigns/${orderId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || 'Traffic failed')
      fetchDashboardData()
      alert(data.queued
        ? `Queued Broadsign job ${data.jobId || ''}`
        : (data.message || 'Approved (Control unset)'))
    } catch (error) {
      console.error('Traffic to Control failed:', error)
      alert(error.message || 'Traffic to Control failed')
    }
  }

  const cancelControlCampaign = async (orderId) => {
    if (!window.confirm('Cancel this campaign in Broadsign Control (drop / deactivate)?')) return
    try {
      const response = await fetch(`/api/io/campaigns/${orderId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || 'Cancel failed')
      fetchDashboardData()
      alert('Cancel queued')
    } catch (error) {
      alert(error.message || 'Cancel failed')
    }
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({ status: newStatus })
      })
      
      if (response.ok) {
        // Refresh data
        fetchDashboardData()
        alert('Order status updated successfully')
      }
    } catch (error) {
      console.error('Error updating order status:', error)
      alert('Error updating order status')
    }
  }

  const deleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      try {
        const response = await fetch(`/api/orders/${orderId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${await getAuthToken()}`
          }
        })
        
        if (response.ok) {
          fetchDashboardData()
          alert('Order deleted successfully')
        }
      } catch (error) {
        console.error('Error deleting order:', error)
        alert('Error deleting order')
      }
    }
  }

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Permanently delete user ${email}? This cannot be undone.`)) {
      return
    }
    try {
      const resp = await fetch(`/api/admin/users/${userId}`, { method: 'DELETE' })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        alert(data.error || 'Failed to delete user')
        return
      }
      await fetchUsers()
      alert('User deleted successfully')
    } catch (e) {
      console.error('Error deleting user:', e)
      alert('Error deleting user')
    }
  }

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      alert('Please select a CSV file')
      return
    }

    setBulkImportLoading(true)
    setBulkImportResult(null)

    try {
      const formData = new FormData()
      formData.append('csv', bulkImportFile)

      // Add timeout to prevent hanging (5 minutes max)
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000)

      let resp
      try {
        resp = await fetch('/api/admin/users/bulk-import', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await getAuthToken()}`
          },
          body: formData,
          signal: controller.signal
        })
        clearTimeout(timeoutId)
      } catch (fetchError) {
        clearTimeout(timeoutId)
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. The file may be too large or the server is taking too long to process.')
        }
        throw fetchError
      }

      if (!resp.ok) {
        // Check if response is HTML (Cloudflare challenge page)
        const contentType = resp.headers.get('content-type') || ''
        if (contentType.includes('text/html')) {
          throw new Error('Request blocked by Cloudflare. Please try again or contact support if this persists.')
        }
        
        const errorData = await resp.json().catch(() => ({ error: `Server error: ${resp.status}` }))
        throw new Error(errorData.error || `Server error: ${resp.status}`)
      }

      const data = await resp.json()

      if (!resp.ok) {
        throw new Error(data.error || 'Bulk import failed')
      }

      setBulkImportResult(data)
      
      // Refresh users list
      await fetchUsers()

      // Show success message
      const { summary } = data
      alert(
        `Bulk import completed!\n\n` +
        `Total rows: ${summary.total}\n` +
        `Successful: ${summary.successful}\n` +
        `Errors: ${summary.errors}\n` +
        `Skipped: ${summary.skipped}`
      )

      // Reset file input
      setBulkImportFile(null)
      const fileInput = document.getElementById('bulk-import-file')
      if (fileInput) fileInput.value = ''

    } catch (e) {
      console.error('Bulk import error:', e)
      alert(`Bulk import failed: ${e.message}`)
      setBulkImportResult({ error: e.message })
    } finally {
      setBulkImportLoading(false)
    }
  }

  const handleBulkResendInvites = async () => {
    if (!confirm('This will resend invite emails to all pending venue users. Continue?')) {
      return
    }

    setBulkResendLoading(true)
    try {
      const resp = await fetch('/api/admin/users/bulk-resend-invites', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`
        }
      })
      const data = await resp.json()

      if (!resp.ok) {
        throw new Error(data.error || 'Bulk resend failed')
      }

      alert(
        `Bulk resend completed!\n\n` +
        `Total pending users: ${data.total}\n` +
        `Successfully sent: ${data.sent}\n` +
        `Failed: ${data.failed}`
      )

      // Refresh users list
      await fetchUsers()
    } catch (e) {
      console.error('Bulk resend error:', e)
      alert(`Bulk resend failed: ${e.message}`)
    } finally {
      setBulkResendLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending_review: 'status-badge status-pending',
      approved: 'status-badge status-completed',
      rejected: 'status-badge status-cancelled',
      in_progress: 'status-badge status-processing',
      completed: 'status-badge status-completed',
      draft: 'status-badge status-pending',
      sent: 'status-badge status-processing',
      accepted: 'status-badge status-completed',
      declined: 'status-badge status-cancelled',
      cancelled: 'status-badge status-cancelled',
    }
    return statusClasses[status] || 'status-badge'
  }

  const filteredOrders = orders.filter(order => {
    const qRaw = (searchTerm || '').toString().trim().toLowerCase()
    const q = qRaw.startsWith('#') ? qRaw.slice(1) : qRaw

    const orderIdStr = (order?.id ?? '').toString().toLowerCase()
    const ticketNumberStr = (order?.zammad_ticket_number ?? '').toString().toLowerCase()
    const ticketIdStr = (order?.zammad_ticket_id ?? '').toString().toLowerCase()

    const matchesSearch = !q
      ? true
      : (
          orderIdStr === q ||
          ticketNumberStr === q ||
          ticketIdStr === q ||
          orderIdStr.includes(q) ||
          ticketNumberStr.includes(q) ||
          ticketIdStr.includes(q) ||
          order.advertiser_name?.toLowerCase().includes(q) ||
          order.campaign_name?.toLowerCase().includes(q) ||
          order.contact_email?.toLowerCase().includes(q) ||
          order.recipient_email?.toLowerCase().includes(q) ||
          order.user_email?.toLowerCase().includes(q) ||
          order.venue_uid?.toLowerCase().includes(q)
        )

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter

    return matchesSearch && matchesStatus
  })

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Initializing authentication...</span>
      </div>
    )
  }

  // Show access denied if authenticated but not admin or venue user
  if (isAuthenticated && !isAdmin && !(user && user.userType === 'venue')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You don't have permission to access the admin dashboard.
          </p>
          <div className="text-sm text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-gray-700 rounded p-3">
            <p><strong>Logged in as:</strong> {user?.name || user?.mail || 'Unknown User'}</p>
            <p className="mt-1">Contact your administrator to request admin access.</p>
          </div>
        </div>
      </div>
    )
  }

  // Show loading for dashboard data
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading dashboard...</span>
      </div>
    )
  }

  // Main admin dashboard (only shown to authenticated admins)
  return (
    <div className="space-y-8">
      {/* FIXED: Header - Clean design with proper DB Fix button placement */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            {/* Left Side - Context Networks Logo */}
            <div className="flex items-center">
              <div className="w-64 h-64 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-lg border border-gray-200 dark:border-gray-600 p-4">
                <img 
                  src="/images/context-networks-logo.png" 
                  alt="Context Networks"
                  className="w-60 h-60 object-contain"
                />
              </div>
            </div>

            {/* Center - Title only */}
            <div className="text-center flex-1">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Order Insertion Admin Portal
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Monitor campaign orders, RFPs, and user access
                </p>
              </div>
            </div>

            {/* Right Side - User Info, Back button & Database Fix */}
            <div className="w-40 flex flex-col items-end space-y-3">
              <div className="flex items-center space-x-2 text-green-600 dark:text-green-400 text-sm">
                <Shield className="w-4 h-4" />
                <span>Admin Access</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-right">
                {user?.name || user?.mail}
              </span>

              {/* Back to App */}
              <button
                onClick={() => { try { window.localStorage.setItem('adv_view_as_app','1'); } catch {}; window.location.href = '/app?view=app'; }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600"
                title="Return to the main app"
              >
                <span>View App</span>
              </button>
              
              {/* Optional DB Fix button - keep hidden unless needed */}
              {/* <button
                onClick={() => setShowDbFix(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-800 transition-colors border-2 border-red-300 dark:border-red-700"
                title="Fix database schema if orders are failing to submit"
              >
                <Database className="w-4 h-4" />
                <span>DB Fix</span>
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.totalOrders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.ioTotal} IO{stats.rfpCount ? ` · ${stats.rfpCount} RFP` : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-success-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Budget</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">${stats.totalRevenue?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-warning-100 rounded-lg">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.pendingOrders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">IO — pending review</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-success-100 rounded-lg">
              <Users className="w-6 h-6 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Approved</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.completedOrders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">IO — approved</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <FileText className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Other statuses</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.otherOrders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rejected, in progress, etc.</p>
            </div>
          </div>
        </div>
      </div>

      {ordersFetchError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 dark:text-red-200 font-medium">Orders could not be loaded</p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{ordersFetchError}</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              If this started after a deploy, restart the app or use the DB Fix action so missing columns are applied.
            </p>
          </div>
        </div>
      )}

      {/* FIXED: Updated Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Campaign Orders
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Users & Invites
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="card">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Campaign Orders</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">{filteredOrders.length} orders</span>
            </div>
            
              <div className="flex items-center space-x-4 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              <select
                value={recordKindFilter}
                onChange={(e) => setRecordKindFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">IO + RFP</option>
                <option value="io">Campaign orders (IO)</option>
                <option value="rfp">RFPs only</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending_review">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="draft">RFP — Draft</option>
                <option value="sent">RFP — Sent</option>
                <option value="accepted">RFP — Accepted</option>
                <option value="declined">RFP — Declined</option>
                <option value="cancelled">RFP — Cancelled</option>
              </select>

              {/* Export Button */}
              <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-responsive">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Order ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Type</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Ticket #</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Advertiser</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Recipient</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Submitted by</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Campaign</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Ad Type</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Budget / quoted</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Sent</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'No orders match your search criteria' 
                        : 'No orders found. When orders are submitted, they\'ll appear here.'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4 font-mono text-sm text-gray-900 dark:text-white">{order.id}</td>
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">
                        {order.record_kind === 'rfp' ? 'RFP' : 'IO'}
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-gray-900 dark:text-white">
                        {order.zammad_ticket_number
                          ? `#${order.zammad_ticket_number}`
                          : (order.zammad_ticket_id ? 'Pending' : '—')}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        <div>
                          <div className="font-medium">{order.advertiser_name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{order.contact_email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                        {order.recipient_email ? (
                          <div>
                            <div>{order.recipient_name || '—'}</div>
                            <div className="text-gray-500">{order.recipient_email}</div>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400 break-all max-w-[140px]">
                        {order.user_email || '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{order.campaign_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{order.ad_type}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {order.record_kind === 'rfp' && order.quoted_price != null
                          ? `$${Number(order.quoted_price).toLocaleString()}`
                          : order.budget
                            ? `$${Number(order.budget).toLocaleString()}`
                            : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {/* Venue users can see status but cannot change it (read-only) */}
                        {isAdmin ? (
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 ${getStatusBadge(order.status)}`}
                          >
                            {order.record_kind === 'rfp' ? (
                              <>
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="accepted">Accepted</option>
                                <option value="declined">Declined</option>
                                <option value="cancelled">Cancelled</option>
                              </>
                            ) : (
                              <>
                                <option value="pending_review">Pending Review</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </>
                            )}
                          </select>
                        ) : (
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getStatusBadge(order.status)}`}>
                            {order.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(order.submitted_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {order.sent_at ? new Date(order.sent_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => { window.location.href = `/status/${order.id}` }}
                            className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm"
                            title="View Order"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isAdmin && order.record_kind !== 'rfp' && (
                            <>
                              <button
                                type="button"
                                onClick={() => trafficToControl(order.id)}
                                className="text-xs text-blue-700 dark:text-blue-300 underline"
                                title="Approve and traffic to Broadsign Control"
                              >
                                Traffic
                              </button>
                              {order.broadsign_reservation_id && (
                                <button
                                  type="button"
                                  onClick={() => cancelControlCampaign(order.id)}
                                  className="text-xs text-red-600 underline"
                                  title="Cancel Control campaign"
                                >
                                  Drop
                                </button>
                              )}
                            </>
                          )}
                          {order.record_kind === 'rfp' && (
                            <a
                              href={`/api/orders/${order.id}/rfp-pdf`}
                              className="text-primary-600 hover:text-primary-700 text-sm"
                              title="Download RFP PDF"
                              target="_blank"
                              rel="noreferrer"
                            >
                              <FileText className="w-4 h-4" />
                            </a>
                          )}
                          {/* Only admins can delete orders */}
                          {isAdmin && (
                            <button
                              onClick={() => deleteOrder(order.id)}
                              className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm"
                              title="Delete Order"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* NEW: Users & Invites Management */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Users & Invites</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Create invites, resend or revoke, and activate/deactivate users.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {/* Search field */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users, venues, publishers..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-64"
                  />
                </div>
                <button
                  onClick={fetchUsers}
                  className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <input
                type="email"
                placeholder="Invite email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <input
                type="text"
                placeholder="Display name (required)"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                required
                aria-required="true"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {/* Account Type selector */}
              <div className="flex items-center">
                <label className="mr-2 text-sm text-gray-700 dark:text-gray-300">Account</label>
                <select
                  value={inviteAccountType}
                  onChange={(e) => {
                    setInviteAccountType(e.target.value)
                    if (e.target.value !== 'venue') {
                      setInviteVenueQuery('')
                      setInviteVenueUids([])
                      setInviteVenueMap({})
                      setInviteVenueOptions([])
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  title="Account type"
                >
                  <option value="paying">Brand/Agency (paying)</option>
                  <option value="venue">Venue</option>
                  <option value="admin">Admin (staff)</option>
                </select>
              </div>
              <button
                onClick={async () => {
                  try {
                    setInviting(true)
                    if (!inviteName || !String(inviteName).trim()) {
                      alert('Display name is required.')
                      setInviting(false)
                      return
                    }
                    if (inviteAccountType === 'venue' && (!inviteVenueUids || inviteVenueUids.length === 0)) {
                      alert('Please select at least one venue for venue accounts.')
                      setInviting(false)
                      return
                    }
                    if (inviteAccountType === 'affiliate' && (!invitePmpIds || invitePmpIds.length === 0)) {
                      alert('Select at least one PMP for affiliate accounts.')
                      setInviting(false)
                      return
                    }
                    const resp = await fetch('/api/admin/users/invite', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 
                        email: inviteEmail, 
                        displayName: inviteName, 
                        accountType: inviteAccountType, 
                        venueUids: inviteAccountType === 'venue' ? inviteVenueUids : undefined,
                        allowedPmpIds: inviteAccountType === 'affiliate' ? invitePmpIds : undefined,
                      })
                    })
                    const data = await resp.json()
                    if (!resp.ok) throw new Error(data.error || 'Invite failed')
                    await fetchUsers()
                    let msg = inviteVenueUids && inviteVenueUids.length > 1 
                      ? `Invites created for ${inviteVenueUids.length} venues.`
                      : `Invite created.`
                    if (data.invite?.link) {
                      const copied = await copyText(data.invite.link)
                      msg += copied ? ` Link copied to clipboard.` : ` Link: ${data.invite.link}`
                    }
                    if (data.emailConfigured) {
                      msg += data.emailSent ? ' Email sent.' : ' Email NOT sent (SMTP error).'
                    } else {
                      msg += ' Email not configured.'
                    }
                    alert(msg)
                    // reset invite form
                    setInviteEmail('')
                    setInviteName('')
                    setInviteAccountType('paying')
                    setInviteVenueQuery('')
                    setInviteVenueUids([])
                    setInviteVenueMap({})
                    setInvitePmpIds([])
                  } catch (e) {
                    alert(e.message)
                  } finally {
                    setInviting(false)
                  }
                }}
                disabled={inviting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium"
              >
                {inviting ? 'Inviting…' : 'Create Invite'}
              </button>
            </div>
            {inviteAccountType === 'affiliate' && (
              <div className="mt-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allowed PMPs *</p>
                {!pmpCatalog.length ? (
                  <p className="text-xs text-gray-500">Loading PMP catalog…</p>
                ) : (
                  pmpCatalog.map((p) => (
                    <label key={p.pmp_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={invitePmpIds.includes(p.pmp_id)}
                        onChange={() => {
                          setInvitePmpIds((prev) =>
                            prev.includes(p.pmp_id) ? prev.filter((x) => x !== p.pmp_id) : [...prev, p.pmp_id]
                          )
                        }}
                      />
                      {p.label}
                    </label>
                  ))
                )}
              </div>
            )}
            {inviteAccountType === 'venue' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign Venues (inventory / Publisher Onboarding — select multiple)</label>
                {(!inventoryStatus.configured || !inventoryStatus.connected) && (
                  <div className="mb-2 text-xs text-amber-700 dark:text-amber-300">
                    Inventory database not connected. Venue list may be empty.
                    <button onClick={fetchInventoryStatus} className="ml-2 underline">Refresh</button>
                  </div>
                )}
                
                {/* Selected venues display */}
                {inviteVenueUids.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {inviteVenueUids.map((uid) => (
                      <span
                        key={uid}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                      >
                        {inviteVenueMap[uid] ? formatVenueLabel(inviteVenueMap[uid]) : uid}
                        <button
                          type="button"
                          onClick={() => {
                            setInviteVenueUids(prev => prev.filter(u => u !== uid))
                            setInviteVenueMap(prev => {
                              const newMap = { ...prev }
                              delete newMap[uid]
                              return newMap
                            })
                          }}
                          className="ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Type at least 2 characters to search publishers..."
                    value={inviteVenueQuery}
                    onChange={(e) => {
                      setInviteVenueQuery(e.target.value)
                      setInviteVenueMenuOpen(true)
                    }}
                    onFocus={() => setInviteVenueMenuOpen(true)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setInviteVenueMenuOpen(!inviteVenueMenuOpen)
                      if (!inviteVenueMenuOpen && inviteVenueBrowse.length === 0) {
                        // Trigger loading of browse list if not already loaded
                        setInviteVenueQuery('')
                      }
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {inviteVenueLoading && (
                    <div className="absolute right-3 top-2.5 text-xs text-gray-500">Searching…</div>
                  )}
                  {inviteVenueMenuOpen && (!inviteVenueSearchActive ? inviteVenueBrowse.length > 0 : true) && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                      {inviteVenueSearchActive && inviteVenueDisplayList.length === 0 && !inviteVenueLoading && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No venues matched this search.</div>
                      )}
                      {inviteVenueDisplayList.map((v) => {
                        const isSelected = inviteVenueUids.includes(v.uid)
                        return (
                          <div
                            key={v.uid}
                            className={`w-full flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                            onClick={() => {
                              if (isSelected) {
                                setInviteVenueUids(prev => prev.filter(u => u !== v.uid))
                                setInviteVenueMap(prev => {
                                  const newMap = { ...prev }
                                  delete newMap[v.uid]
                                  return newMap
                                })
                              } else {
                                setInviteVenueUids(prev => [...prev, v.uid])
                                setInviteVenueMap(prev => ({ ...prev, [v.uid]: v }))
                              }
                              // Keep menu open for multi-selection
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}} // Handled by parent onClick
                              className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="flex-1">{formatVenueLabel(v)}</span>
                          </div>
                        )
                      })}
                      {!inviteVenueSearchActive && inviteVenueBrowse.length >= 50 && (
                        <button
                          type="button"
                          onClick={() => setInviteVenuePage(inviteVenuePage + 1)}
                          className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Load more…
                        </button>
                      )}
                    </div>
                  )}
                  {inviteVenueSearchActive && inviteVenueOptions.length === 0 && !inviteVenueLoading && inviteVenueMenuOpen && (
                    <div className="mt-1 text-xs text-gray-500">If this venue is missing, confirm <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">INVENTORY_DATABASE_URL</code> points at the onboarding Postgres and the venue has <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">venue_uid</code>.</div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Bulk Import Section - Separate Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bulk Import Venues</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Upload a CSV file to import multiple venues and send invites automatically.
                </p>
              </div>
              <button
                onClick={() => setShowBulkImport(!showBulkImport)}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                <Upload className="w-4 h-4 mr-2" />
                {showBulkImport ? 'Hide' : 'Show Import'}
              </button>
            </div>
            
            {showBulkImport && (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">CSV Format Requirements</h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                          Your CSV file should include these columns (case-insensitive):
                        </p>
                        <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                          <li><strong>Email</strong> (required) - Venue contact email</li>
                          <li><strong>Name</strong> (required) - Venue name (used to generate Location Code)</li>
                          <li><strong>Address</strong> (required) - Street address (used to generate Location Code)</li>
                          <li><strong>Contact</strong> - Contact person name (used as display name, matches single invite form)</li>
                          <li><strong>Owner</strong> - Owner name (fallback for display name if Contact is empty)</li>
                          <li><strong>City</strong> - City (optional)</li>
                          <li><strong>State</strong> - State (optional)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-800 dark:text-blue-200">
                          <strong>Location Code Generation:</strong> Venue UID is automatically generated using the format <code>VEN-S###</code> where:
                          <ul className="list-disc list-inside mt-1 ml-2">
                            <li><strong>VEN</strong> = First 3 letters of venue name (e.g., "Bremen Cafe" → "BRE")</li>
                            <li><strong>S</strong> = First letter of street name (e.g., "Clarke St" → "C")</li>
                            <li><strong>###</strong> = Last 3 digits of street address (e.g., "901" → "901")</li>
                          </ul>
                          <p className="mt-1">Example: Bremen Cafe at 901 E Clarke St → <code>BRE-C901</code></p>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Other columns (Zip Code, Company Phone, Owner Phone, Number) are accepted but not required. 
                          All imported users will be created as <strong>Venue</strong> account type.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex-1">
                      <input
                        type="file"
                        id="bulk-import-file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setBulkImportFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500 dark:text-gray-400
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100
                          dark:file:bg-blue-900/30 dark:file:text-blue-300
                          dark:hover:file:bg-blue-900/50"
                      />
                    </label>
                    <button
                      onClick={handleBulkImport}
                      disabled={!bulkImportFile || bulkImportLoading}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center"
                    >
                      {bulkImportLoading ? (
                        <>
                          <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Venues
                        </>
                      )}
                    </button>
                  </div>
                  
                  {bulkImportResult && (
                    <div className={`rounded-lg p-4 ${
                      bulkImportResult.error
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
                        : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                    }`}>
                      {bulkImportResult.error ? (
                        <div className="text-red-800 dark:text-red-200">
                          <strong>Error:</strong> {bulkImportResult.error}
                        </div>
                      ) : bulkImportResult.summary ? (
                        <div className="text-green-800 dark:text-green-200">
                          <h4 className="font-semibold mb-2">Import Summary</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="font-medium">Total:</span> {bulkImportResult.summary.total}
                            </div>
                            <div className="text-green-600 dark:text-green-400">
                              <span className="font-medium">Successful:</span> {bulkImportResult.summary.successful}
                            </div>
                            <div className="text-red-600 dark:text-red-400">
                              <span className="font-medium">Errors:</span> {bulkImportResult.summary.errors}
                            </div>
                            <div>
                              <span className="font-medium">Skipped:</span> {bulkImportResult.summary.skipped}
                            </div>
                          </div>
                          
                          {bulkImportResult.results?.errors?.length > 0 && (
                            <div className="mt-4">
                              <h5 className="font-medium mb-2">Errors:</h5>
                              <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                                {bulkImportResult.results.errors.map((err, idx) => (
                                  <div key={idx} className="text-red-700 dark:text-red-300">
                                    Row {err.row} ({err.email || 'no email'}): {err.error}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* Bulk Resend Invites Section - Always Visible */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resend Pending Invites</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Resend invite emails to all pending venue users who haven't accepted their invites yet.
                </p>
              </div>
              <button
                onClick={handleBulkResendInvites}
                disabled={bulkResendLoading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center"
              >
                {bulkResendLoading ? (
                  <>
                    <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Resend All Pending Invites
                  </>
                )}
              </button>
            </div>
          </div>

            <details open className="mt-4 group bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-between">
                <span>Instructions: what each action does</span>
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 hidden group-open:inline">Click to collapse</span>
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 group-open:hidden">Click to expand</span>
              </summary>
              <div className="px-4 pb-4 pt-1 text-sm text-gray-600 dark:text-gray-400 space-y-3">
                <p>
                  Users no longer have passwords — everyone signs in with a one-time emailed link (staff still use Azure SSO). These actions map to different points in a user's lifecycle, not different ways of doing the same thing:
                </p>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">Invite / Resend</p>
                  <p>Sends a one-time sign-in link by email. <strong>Invite</strong> shows up for a user with no outstanding link yet; <strong>Resend</strong> shows up once one exists. Resending generates a brand-new link and immediately replaces the old one — only the newest link will ever work.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">Revoke</p>
                  <p>Only shown on a pending (not-yet-accepted) invite. Clears that outstanding link right away <em>without</em> sending a replacement — use this when a link went to the wrong inbox, you invited someone by mistake, or you need details fixed (venue, name) before anyone gets a new link. The user record stays, so you can correct it and re-invite when ready.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">Deactivate / Activate</p>
                  <p>For a user who has already signed in at least once. <strong>Deactivate</strong> blocks their login but keeps their record and everything tied to it — order history, campaigns, audit trail. Use it when someone leaves a venue or needs to be paused temporarily. <strong>Activate</strong> reverses it instantly with no data loss.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">Delete</p>
                  <p><strong>Permanent — cannot be undone.</strong> Use only for a record that should never have existed at all: a duplicate, a test entry, a wrong email. Never use it on a user with real order or campaign history; use Deactivate instead.</p>
                </div>
              </div>
            </details>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="mb-4 flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {(() => {
                  const filteredUsers = adminUsers.filter(u => {
                    const searchLower = userSearchTerm.toLowerCase();
                    return !userSearchTerm || 
                           (u.display_name || '').toLowerCase().includes(searchLower) ||
                           u.email.toLowerCase().includes(searchLower) ||
                           (u.venueName || '').toLowerCase().includes(searchLower) ||
                           (u.publisherName || '').toLowerCase().includes(searchLower) ||
                           (u.venue_uid || '').toLowerCase().includes(searchLower) ||
                           (u.venue_id || '').toString().includes(searchLower);
                  });
                  return userSearchTerm 
                    ? `Showing ${filteredUsers.length} of ${adminUsers.length} users`
                    : `${adminUsers.length} total users`;
                })()}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">User Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Venue Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Venue ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Venue Type</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Invite</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.filter(u => {
                    const searchLower = userSearchTerm.toLowerCase();
                    return !userSearchTerm || 
                           (u.display_name || '').toLowerCase().includes(searchLower) ||
                           u.email.toLowerCase().includes(searchLower) ||
                           (u.venueName || '').toLowerCase().includes(searchLower) ||
                           (u.publisherName || '').toLowerCase().includes(searchLower) ||
                           (u.venue_uid || '').toLowerCase().includes(searchLower) ||
                           (u.venue_id || '').toString().includes(searchLower);
                  }).length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {userSearchTerm 
                          ? `No users match "${userSearchTerm}". Try a different search term.`
                          : 'No users found. Create an invite to get started.'
                        }
                      </td>
                    </tr>
                  ) : adminUsers.filter(u => {
                    const searchLower = userSearchTerm.toLowerCase();
                    return !userSearchTerm || 
                           (u.display_name || '').toLowerCase().includes(searchLower) ||
                           u.email.toLowerCase().includes(searchLower) ||
                           (u.venueName || '').toLowerCase().includes(searchLower) ||
                           (u.publisherName || '').toLowerCase().includes(searchLower) ||
                           (u.venue_uid || '').toLowerCase().includes(searchLower) ||
                           (u.venue_id || '').toString().includes(searchLower);
                  }).map(u => (
                    <tr key={u.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        <div className="font-medium">
                          {(u.display_name && 
                            u.display_name !== u.venueName && 
                            !u.display_name.includes('@') && 
                            u.display_name.trim() !== '') 
                            ? u.display_name 
                            : u.contactPerson || u.email.split('@')[0]}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.account_type === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                            u.account_type === 'venue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {formatAccountType(u.account_type || 'paying')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{u.email}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        <div>
                          <div className="font-medium">{u.venueName || '—'}</div>
                          {u.publisherName && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {u.publisherName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white font-mono text-sm">
                        {u.displayVenueId || '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        <span className="font-medium">
                          {u.venueType || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col space-y-1">
                          {u.accepted_at ? (
                            // User has accepted invite - they are Active
                            <span className={`px-2 py-1 text-xs rounded-full w-fit bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`}>
                              Active
                            </span>
                          ) : (u.invite_token || u.inviteLink || u.invite_expires) ? (
                            // User has pending invite (has invite token but hasn't accepted) - they are Pending
                            <span className={`px-2 py-1 text-xs rounded-full w-fit bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200`}>
                              Pending
                            </span>
                          ) : (
                            // No invite - show based on is_active status
                            <span className={`px-2 py-1 text-xs rounded-full w-fit ${u.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {u.inviteLink ? (
                          <div className="flex items-center space-x-2">
                            <a href={u.inviteLink} className="text-blue-600 dark:text-blue-400 text-xs inline-flex items-center" target="_blank" rel="noreferrer">
                              <Link2 className="w-3 h-3 mr-1" /> Link
                            </a>
                            <button
                              title="Copy invite link"
                              onClick={async () => {
                                const ok = await copyText(u.inviteLink)
                                if (ok) alert('Invite link copied')
                                else alert(`Copy failed. Link: ${u.inviteLink}`)
                              }}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3 text-sm">
                          <button
                            onClick={() => {
                              setEditUser(u)
                              setEditAccountType(u.account_type || 'paying')
                              setEditVenueUid(u.venue_uid || '')
                              // Set venue query to display the current venue name if available
                              if (u.venue_uid && (u.venueName || u.publisherName)) {
                                const venueLabel = u.publisherName && u.venueName 
                                  ? `${u.publisherName} — ${u.venueName}`
                                  : (u.venueName || u.publisherName || '')
                                setEditVenueQuery(venueLabel)
                              } else {
                                setEditVenueQuery('')
                              }
                              setEditVenueOptions([])
                              setEditEmail(u.email || '')
                              setEditDisplayName(
                                (u.display_name &&
                                  u.display_name !== u.venueName &&
                                  !u.display_name.includes('@') &&
                                  u.display_name.trim() !== '')
                                  ? u.display_name
                                  : (u.contactPerson || u.email.split('@')[0])
                              )
                              // Populate existing venues for this email
                              const existingVenues = adminUsers.filter(user => user.email === u.email && user.venue_uid).map(user => ({
                                venue_uid: user.venue_uid,
                                venueName: user.venueName,
                                publisherName: user.publisherName
                              }))
                              setEditExistingVenues(existingVenues)
                              setEditPmpIds((u.affiliatePmps || []).map((p) => p.pmp_id))
                              setEditNewVenueUids([])
                              setEditNewVenueMap({})
                              setEditNewVenueQuery('')
                              setEditNewVenueOptions([])
                              setEditNewVenueBrowse([])
                              setEditNewVenuePage(0)
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Edit
                          </button>
                          {u.inviteLink ? (
                            <button
                              onClick={async () => {
                                const resp = await fetch(`/api/admin/users/${u.id}/resend-invite`, { method: 'POST' })
                                const data = await resp.json()
                                if (!resp.ok) return alert(data.error || 'Failed to resend')
                                await fetchUsers()
                                let msg = 'Invite regenerated.'
                                if (data.invite?.link) {
                                  const copied = await copyText(data.invite.link)
                                  msg += copied ? ' Link copied.' : ` Link: ${data.invite.link}`
                                }
                                if (data.emailConfigured) {
                                  msg += data.emailSent ? ' Email sent.' : ' Email NOT sent.'
                                }
                                alert(msg)
                              }}
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" /> Resend
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                const dn = (u.display_name || '').trim()
                                if (!dn) {
                                  alert('This user has no display name. Edit the user to set a display name before sending an invite.')
                                  return
                                }
                                const resp = await fetch('/api/admin/users/invite', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: u.email, displayName: dn, accountType: u.account_type, venueUids: u.account_type === 'venue' && u.venue_uid ? [u.venue_uid] : undefined }) })
                                const data = await resp.json()
                                if (!resp.ok) return alert(data.error || 'Failed to invite')
                                await fetchUsers()
                                let msg = 'Invite created.'
                                if (data.invite?.link) {
                                  const copied = await copyText(data.invite.link)
                                  msg += copied ? ' Link copied.' : ` Link: ${data.invite.link}`
                                }
                                if (data.emailConfigured) {
                                  msg += data.emailSent ? ' Email sent.' : ' Email NOT sent.'
                                }
                                alert(msg)
                              }}
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Invite
                            </button>
                          )}
                          {u.inviteLink && (
                            <button
                              onClick={async () => {
                                const resp = await fetch(`/api/admin/users/${u.id}/revoke-invite`, { method: 'POST' })
                                const data = await resp.json()
                                if (!resp.ok) return alert(data.error || 'Failed to revoke')
                                await fetchUsers()
                              }}
                              className="text-amber-600 hover:text-amber-800"
                            >
                              Revoke
                            </button>
                          )}
                          {u.is_active ? (
                            <button
                              onClick={async () => {
                                const resp = await fetch(`/api/admin/users/${u.id}/deactivate`, { method: 'POST' })
                                const data = await resp.json()
                                if (!resp.ok) return alert(data.error || 'Failed to deactivate')
                                await fetchUsers()
                              }}
                              className="text-red-600 hover:text-red-800 inline-flex items-center"
                            >
                              <UserX className="w-4 h-4 mr-1" /> Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                const resp = await fetch(`/api/admin/users/${u.id}/activate`, { method: 'POST' })
                                const data = await resp.json()
                                if (!resp.ok) return alert(data.error || 'Failed to activate')
                                await fetchUsers()
                              }}
                              className="text-green-600 hover:text-green-800 inline-flex items-center"
                            >
                              <UserCheck className="w-4 h-4 mr-1" /> Activate
                            </button>
                          )}
                          <button
                            onClick={() => deleteUser(u.id, u.email)}
                            className="text-red-600 hover:text-red-800 inline-flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Database Fix Modal */}
      {showDbFix && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Database className="w-5 h-5 text-blue-500 mr-2" />
                Database Schema Fix
              </h3>
              <button
                onClick={() => setShowDbFix(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If order submissions are failing with "column does not exist" errors, this will add the missing database columns.
              </p>
              
              {dbFixResult && (
                <div className={`p-3 rounded-lg mb-4 ${
                  dbFixResult.success 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                }`}>
                  <p className="font-medium">
                    {dbFixResult.success ? '✅ Success' : '❌ Error'}
                  </p>
                  <p className="text-sm mt-1">{dbFixResult.message || dbFixResult.error}</p>
                  {dbFixResult.columnAdded && (
                    <p className="text-xs mt-1">Column form_data_json was added to orders table</p>
                  )}
                </div>
              )}
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
                <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                  <strong>⚠️ Note:</strong> This is safe to run multiple times. It will only add missing columns if they don't already exist.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDbFix(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDatabaseFix}
                disabled={dbFixLoading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium flex items-center"
              >
                {dbFixLoading ? (
                  <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                {dbFixLoading ? 'Fixing...' : 'Fix Database Schema'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full my-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Edit User</h3>
              <button onClick={() => setEditUser(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display name / username</label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Display name used in app"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                <select
                  value={editAccountType}
                  onChange={(e) => {
                    setEditAccountType(e.target.value)
                    if (e.target.value !== 'venue') setEditVenueUid('')
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="paying">Brand/Agency (paying)</option>
                  <option value="venue">Venue</option>
                  <option value="admin">Admin (staff)</option>
                </select>
              </div>
              {editAccountType === 'affiliate' && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allowed PMPs</p>
                  {pmpCatalog.map((p) => (
                    <label key={p.pmp_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editPmpIds.includes(p.pmp_id)}
                        onChange={() => {
                          setEditPmpIds((prev) =>
                            prev.includes(p.pmp_id) ? prev.filter((x) => x !== p.pmp_id) : [...prev, p.pmp_id]
                          )
                        }}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              )}
              {editAccountType === 'venue' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign Venue</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type at least 2 characters to search publishers..."
                      value={editVenueQuery}
                      onChange={(e) => { setEditVenueQuery(e.target.value); setEditVenueMenuOpen(true) }}
                      onFocus={() => setEditVenueMenuOpen(true)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setEditVenueMenuOpen(!editVenueMenuOpen)
                        if (!editVenueMenuOpen && editVenueBrowse.length === 0) {
                          // Trigger loading of browse list if not already loaded
                          setEditVenueQuery('')
                        }
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {editVenueLoading && (
                      <div className="absolute right-3 top-2.5 text-xs text-gray-500">Searching…</div>
                    )}
                    {editVenueMenuOpen && (!editVenueSearchActive ? editVenueBrowse.length > 0 : true) && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                        {editVenueSearchActive && editVenueDisplayList.length === 0 && !editVenueLoading && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No venues matched this search.</div>
                        )}
                        {editVenueDisplayList.map((v) => (
                          <button
                            key={v.uid}
                            type="button"
                            onClick={() => { editVenueSkipNextSearchRef.current = true; setEditVenueUid(v.uid); const text = formatVenueLabel(v); setEditVenueQuery(text); setEditVenueOptions([]); setEditVenueBrowse([]); setEditVenueMenuOpen(false) }}
                            className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm ${editVenueUid === v.uid ? 'bg-blue-50 dark:bg-blue-900/20' : ''} text-gray-800 dark:text-gray-200`}
                          >
                            {formatVenueLabel(v)}
                          </button>
                        ))}
                        {!editVenueSearchActive && editVenueBrowse.length >= 50 && (
                          <button
                            type="button"
                            onClick={loadMoreEditVenues}
                            className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Load more…
                          </button>
                        )}
                      </div>
                    )}
                    {editVenueSearchActive && editVenueOptions.length === 0 && !editVenueLoading && editVenueMenuOpen && (
                      <div className="mt-1 text-xs text-gray-500">No venues matched. Confirm inventory DB URL targets onboarding.</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show existing venue associations and allow adding new ones */}
              {editEmail && editExistingVenues.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Existing Venue Associations</label>
                  <div className="space-y-2">
                    {editExistingVenues.map((venue, idx) => (
                      <div key={idx} className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {venue.venueName || venue.venue_uid}
                        </div>
                        {venue.publisherName && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{venue.publisherName}</div>
                        )}
                        <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">{venue.venue_uid}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add new venue associations */}
              {editEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add Additional Venue Associations</label>
                  {editNewVenueUids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {editNewVenueUids.map((uid) => {
                        const venue = editNewVenueMap[uid]
                        const label = venue ? formatVenueLabel(venue) : uid
                        return (
                          <span key={uid} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                            {label}
                            <button
                              type="button"
                              onClick={() => {
                                setEditNewVenueUids(prev => prev.filter(u => u !== uid))
                                setEditNewVenueMap(prev => {
                                  const newMap = { ...prev }
                                  delete newMap[uid]
                                  return newMap
                                })
                              }}
                              className="ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type at least 2 characters to search publishers..."
                      value={editNewVenueQuery}
                      onChange={(e) => {
                        setEditNewVenueQuery(e.target.value)
                        setEditNewVenueMenuOpen(true)
                      }}
                      onFocus={() => setEditNewVenueMenuOpen(true)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setEditNewVenueMenuOpen(!editNewVenueMenuOpen)
                        if (!editNewVenueMenuOpen && editNewVenueBrowse.length === 0) {
                          setEditNewVenueQuery('')
                        }
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {editNewVenueLoading && (
                      <div className="absolute right-3 top-2.5 text-xs text-gray-500">Searching…</div>
                    )}
                    {editNewVenueMenuOpen && (!editNewVenueSearchActive ? editNewVenueBrowse.length > 0 : true) && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                        {editNewVenueSearchActive && editNewVenueDisplayList.length === 0 && !editNewVenueLoading && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No venues matched this search.</div>
                        )}
                        {editNewVenueDisplayList.map((v) => {
                          const isExisting = editExistingVenues.some(ev => ev.venue_uid === v.uid)
                          const isSelected = editNewVenueUids.includes(v.uid)
                          if (isExisting) return null
                          return (
                            <label
                              key={v.uid}
                              className={`flex items-center w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''} text-gray-800 dark:text-gray-200 cursor-pointer`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    if (!editNewVenueUids.includes(v.uid)) {
                                      setEditNewVenueUids(prev => [...prev, v.uid])
                                      setEditNewVenueMap(prev => ({ ...prev, [v.uid]: v }))
                                    }
                                  } else {
                                    setEditNewVenueUids(prev => prev.filter(u => u !== v.uid))
                                    setEditNewVenueMap(prev => {
                                      const newMap = { ...prev }
                                      delete newMap[v.uid]
                                      return newMap
                                    })
                                  }
                                }}
                                className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span>{formatVenueLabel(v)}</span>
                            </label>
                          )
                        })}
                        {!editNewVenueSearchActive && editNewVenueBrowse.length >= 50 && (
                          <button
                            type="button"
                            onClick={loadMoreEditNewVenues}
                            className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Load more…
                          </button>
                        )}
                      </div>
                    )}
                    {editNewVenueSearchActive && editNewVenueOptions.length === 0 && !editNewVenueLoading && editNewVenueMenuOpen && (
                      <div className="mt-1 text-xs text-gray-500">No venues matched. Confirm inventory DB URL targets onboarding.</div>
                    )}
                  </div>
                  {editNewVenueUids.length > 0 && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setEditAddingVenues(true)
                          const resp = await fetch('/api/admin/users/add-venues', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              email: editEmail,
                              venueUids: editNewVenueUids
                            })
                          })
                          const data = await resp.json()
                          if (!resp.ok) throw new Error(data.error || 'Failed to add venues')
                          await fetchUsers()
                          // Refresh existing venues
                          const updatedUsers = await fetch('/api/admin/users').then(r => r.json()).catch(() => [])
                          const existingVenues = updatedUsers.filter(user => user.email === editEmail && user.venue_uid).map(user => ({
                            venue_uid: user.venue_uid,
                            venueName: user.venueName,
                            publisherName: user.publisherName
                          }))
                          setEditExistingVenues(existingVenues)
                          setEditNewVenueUids([])
                          setEditNewVenueMap({})
                          setEditNewVenueQuery('')
                          alert('Venue associations added successfully')
                        } catch (e) {
                          alert(e.message)
                        } finally {
                          setEditAddingVenues(false)
                        }
                      }}
                      disabled={editAddingVenues}
                      className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium flex items-center"
                    >
                      {editAddingVenues ? (
                        <>
                          <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add {editNewVenueUids.length} Venue{editNewVenueUids.length > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button onClick={() => setEditUser(null)} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">Cancel</button>
              <button
                onClick={async () => {
                  try {
                    setEditSaving(true)
                    if (editAccountType === 'venue' && !editVenueUid) {
                      alert('Please select a venue for venue accounts.')
                      setEditSaving(false)
                      return
                    }
                    const resp = await fetch(`/api/admin/users/${editUser.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email: editEmail,
                        displayName: editDisplayName,
                        accountType: editAccountType,
                        venueUid: editAccountType === 'venue' ? editVenueUid : null,
                        allowedPmpIds: editAccountType === 'affiliate' ? editPmpIds : undefined,
                      })
                    })
                    const data = await resp.json()
                    if (!resp.ok) throw new Error(data.error || 'Failed to update user')
                    await fetchUsers()
                    setEditUser(null)
                  } catch (e) {
                    alert(e.message)
                  } finally {
                    setEditSaving(false)
                  }
                }}
                disabled={editSaving}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium flex items-center"
              >
                {editSaving ? (
                  <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
